import { Component, h } from 'preact'
import IdeaView from './IdeaView'
import Axios from 'axios'
import { ProductAreaType } from '../../control-components/product-areas/ProductAreaSelect'
import Phrase from '../../libs/translationsUtils/phrase'
import qs from 'qs'
import Dropdown from '../dropdown/index'
import ChevronDown from '../assets/icon-chevron-down'
import InputGroup from '../../control-components/ui-components/InputGroup'
import FilterPill from '../filter-pill/FilterPill'

interface Props {
  isGuest: boolean
  ideas: {
    topics: any[]
    total: number
  }
  currentPage: string
  totalPageCount: number
  productAreas?: ProductAreaType[]
  currentUserId: number
  filters: null | {
    ideationStatus?: string[]
  }
  totalTopicCount: number
  selectedSortOption: string
}

type filterTypes = null | {
  ideationStatus?: string[]
  productArea?: string[]
}

interface State {
  ideas: any[]
  currentPage: number
  totalPageCount: number
  isLoading: boolean
  filters: filterTypes
  totalTopicCount: number
  selectedSortOption: string
  currentFilters: filterTypes
}

class IdeasOverview extends Component<Props, State> {
  isPageNumberIncreased: boolean = false
  showBackButton: boolean = false
  productAreaMapping: {
    [key: string]: string
  } = {}

  sortOptions: {
    [key: string]: string
  } = {
    lastpost: 'sort.thread.lastpost.desc',
    dateline: 'sort.thread.dateline.desc',
    replycount: 'sort.thread.replycount.desc',
    votes: 'sort.thread.votes.desc'
  }

  filtersList = ['productArea', 'ideationStatus']

  constructor (props: Props) {
    super(props)
    const params = this.prepareParametersForRequest()

    this.state = {
      isLoading: false,
      ideas: props.ideas.topics,
      currentPage: parseInt(props.currentPage),
      totalPageCount: props.totalPageCount,
      filters: props.filters,
      totalTopicCount: props.totalTopicCount,
      selectedSortOption: props.selectedSortOption || 'lastpost',
      currentFilters: params.filters
    }

    if (props.productAreas?.length) {
      (props.productAreas ?? []).forEach(productArea => { this.productAreaMapping[productArea.name] = productArea.id })
    }

    this.showBackButton = parseInt(props.currentPage) !== 1

    this.setQueryParams = this.setQueryParams.bind(this)
    this.setSortOption = this.setSortOption.bind(this)
    this.getNewTopicsList = this.getNewTopicsList.bind(this)
    this.prepareParametersForRequest = this.prepareParametersForRequest.bind(this)
    this.setPagination = this.setPagination.bind(this)
    this.handleBackNavigation = this.handleBackNavigation.bind(this)
    this.updateFilters = this.updateFilters.bind(this)
  }

  setQueryParams (newQuery: { page?: number, filters?: Object, sort?: string }) {
    const searchQuery = window.location.search
    const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })

    const combinedQuery = { ...queryParameters, ...newQuery }

    if (combinedQuery?.page === 1) {
      delete combinedQuery?.page
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    window.history.pushState('', '', `?${qs.stringify(combinedQuery)}`)
  }

  setSortOption (sortKey: string) {
    this.setState({
      selectedSortOption: sortKey
    }, () => this.setQueryParams({ page: this.state.currentPage, sort: `${this.state.selectedSortOption}.desc` }))
  }

  setPagination (newPage: number) {
    if (newPage > this.state.currentPage) {
      this.isPageNumberIncreased = true
    }
    this.setState({
      currentPage: newPage
    }, () => this.setQueryParams({ page: this.state.currentPage }))
  }

  prepareParametersForRequest () {
    const searchQuery = window.location.search
    const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })
    const filters = queryParameters.filters || {}

    if (filters.productArea) {
      filters.productArea = filters.productArea.map((productAreaName: string) => this.productAreaMapping[productAreaName])
    }

    let filterKeys = Object.keys(filters)
    filterKeys = filterKeys.filter(key => filters[key] !== null)

    const requestFilters = filterKeys.reduce((acc: {[key: string]: Array<string | null>}, newFilter) => {
      acc[newFilter] = filters[newFilter]
      return acc
    }, {})

    return {
      page: queryParameters.page || 1,
      filters: requestFilters,
      sort: {
        [this.state.selectedSortOption]: 'desc'
      }
    }
  }

  async getNewTopicsList () {
    const params = this.prepareParametersForRequest()

    this.setState({ isLoading: true })

    try {
      const res = await Axios.get('/ideas/topics', {
        params
      })

      if (res.data) {
        // Hide show back button if filters are changing except pagination
        if (!this.isPageNumberIncreased) {
          this.showBackButton = false
        }

        this.setState({
          ideas: this.isPageNumberIncreased ? this.state.ideas.concat(res.data.topics) : res.data.topics,
          currentPage: this.isPageNumberIncreased ? this.state.currentPage : 1,
          totalPageCount: res.data.totalPageCount,
          totalTopicCount: res.data.totalTopicCount,
          isLoading: false,
          currentFilters: params.filters
        })
        this.isPageNumberIncreased = false
      }
    } catch {
      this.isPageNumberIncreased = false
      this.setState({ isLoading: false })
    }
  }

  handleBackNavigation () {
    window.location.reload()
  }

  componentDidMount () {
    (function (history) {
      const pushState = history.pushState
      window.history.pushState = function (state) {
        if (typeof window.history.onpushstate === 'function') {
          window.history.onpushstate({
            state: state
          })
        }
        // @ts-expect-error
        return pushState.apply(history, arguments)
      }
    })(window.history)
    window.history.onpushstate = () => {
      // pushstate is async event, without setTimeout it is getting previous state
      window.setTimeout(async () => await this.getNewTopicsList(), 0)
    }
    window.addEventListener('popstate', this.handleBackNavigation)
  }

  componentWillUnmount () {
    window.removeEventListener('popstate', this.handleBackNavigation)
  }

  updateFilters (filter: string) {
    if (filter === 'ideationStatus') {
      window.dispatchEvent(new CustomEvent('ideationStatusFilterRemoval'))
    } else if (filter === 'productArea') {
      window.dispatchEvent(new CustomEvent('productAreaFilterRemoval'))
    }
  }

  render ({ isGuest, currentUserId }: Props, { ideas, currentPage, isLoading, totalPageCount, totalTopicCount, selectedSortOption }: State) {
    const searchQuery = window.location.search
    const filters = qs.parse(searchQuery, { ignoreQueryPrefix: true }).filters || {}
    const filterKeys: string[] = Object.keys(filters).filter(filter => this.filtersList.includes(filter))
    if (filterKeys.length) {
      document.body.classList.add('filters-applied')
    } else {
      document.body.classList.remove('filters-applied')
    }
    return (
      <div className='ideation-overview-list widget-wrapper'>
        {
          filterKeys.length
            ? (
              <ul className='filter-list is-hidden-L'>
                {
                filterKeys.map(filter => (
                  <FilterPill
                    key={filters[filter][0]}
                    name={filters[filter][0]}
                    onClick={() => this.updateFilters(filter)}
                    isDismissable
                  />
                ))
              }
                {
                filterKeys.length > 1
                  ? (
                    <li className='link link--underline filter-reset-link' onClick={() => { window.dispatchEvent(new CustomEvent('resetFilters')) }}>
                      <Phrase m='Common' p='advanced.search.filter.clear.all' />
                    </li>
                    )
                  : null
              }
              </ul>
              )
            : null
        }
        <header className='overview-list-header'>
          <h3 className='widget__heading'> {totalTopicCount} Ideas</h3>
          <nav>
            <Dropdown
              popupName='ideation-list-sort'
              class='dropdown dropdown--sort-options'
              closeOnDropdownClick
              triggerClass='dropdown--sort-options-trigger'
              trigger={
                <span style={{ display: 'flex', alignItems: 'center' }} className='sort-option-trigger'>
                  <span>
                    <Phrase p={this.sortOptions[selectedSortOption]} />
                  </span>
                  <ChevronDown />
                </span>
              }
            >
              {
                Object.keys(this.sortOptions).filter(option => option !== selectedSortOption)
                  .map(option => (
                    <div
                      key={option}
                      className='dropdown--sort-option'
                      onClick={() => this.setSortOption(option)}
                    >
                      <Phrase p={this.sortOptions[option]} />
                    </div>
                  ))
              }
            </Dropdown>
          </nav>
        </header>
        {
          ideas.length
            ? (
              <div className='list-widget-wrapper'>
                {
                this.showBackButton
                  ? (
                    <div className='load-more-container load-more-topics'>
                      <button
                        className={`btn--cta btn--full-width btn--load-more ${isLoading ? 'is-loading disabled' : ''}`}
                        onClick={() => this.setPagination(1)} type='button'
                      >
                        Back to top of the list
                        <span className='loader' />
                      </button>
                    </div>
                    )
                  : null
              }
                {
                ideas.map(idea => (
                  <IdeaView
                    key={idea.id}
                    {...idea}
                    isGuest={isGuest}
                    currentUserId={currentUserId}
                  />
                ))
              }
                {
                currentPage < totalPageCount
                  ? (
                    <div className='load-more-container load-more-topics'>
                      <button
                        className={`btn--cta btn--full-width btn--load-more ${isLoading ? 'is-loading disabled' : ''}`}
                        onClick={() => this.setPagination(currentPage + 1)} type='button'
                      >
                        <Phrase p='Show more' />
                        <span className='loader' />
                        <a href={`/ideas?page=${currentPage + 1}`} />
                      </button>
                    </div>
                    )
                  : null
              }
              </div>
              )
            : (
              <div
                className='box thread-list-block thread-list-block-empty-status thread-list-block--meta forum-list-view-spacing forum-list-view-spacing--v-s'
              >
                <p className='p--full-width'>
                  No result found
                </p>

                <a href='/topic/new' className='btn--cta btn--new-topic qa-topic-new-button'>
                  <Phrase p='main.navigation.add_topic' m='Forum' />
                </a>
              </div>
              )
        }
      </div>
    )
  }
}

interface PropsForConfigurator {
  title: string
  onChange: (state: { title: string }) => void
}

interface StateForConfigurator {
  title: string
}

export class ListWithHelp extends Component<PropsForConfigurator, StateForConfigurator> {
  constructor (props: PropsForConfigurator) {
    super(props)
    this.state = {
      title: props.title
    }

    this.onChangeTitle = this.onChangeTitle.bind(this)
  }

  onChangeTitle (newTitle: string) {
    this.setState({ title: newTitle }, () => this.props.onChange(this.state))
  }

  render (_: PropsForConfigurator, { title }: StateForConfigurator) {
    return (
      <div className=''>
        <InputGroup
          title='Title'
          name='title'
          placeholder='Add a title'
          type='text'
          onChange={(e: {target: HTMLInputElement}) => { this.onChangeTitle(e.target.value) }}
          value={title}
        />
      </div>
    )
  }
}

export default IdeasOverview
